import React, { useEffect, useState } from "react";
import { UserPlus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import { Chat } from "@/components/Chat";

export default function MessagesPage() {
  const [convos, setConvos] = useState(null);
  const [conns, setConns] = useState([]);
  const [invites, setInvites] = useState({ incoming: [], outgoing: [] });
  const [active, setActive] = useState(null);
  const [handle, setHandle] = useState("");

  const loadAll = () => {
    TurfTalk.listConversations().then((r) => setConvos(r.conversations || [])).catch(() => setConvos([]));
    TurfTalk.listConnections().then((r) => setConns(r.connections || [])).catch(() => {});
    TurfTalk.connectionInvites().then(setInvites).catch(() => {});
  };
  useEffect(loadAll, []);

  const request = async () => {
    if (!handle.trim()) return;
    try { await TurfTalk.requestConnection(handle.trim().replace(/^@/, "")); toast.success("Connection request sent"); setHandle(""); loadAll(); }
    catch (e) { toast.error(e.message); }
  };
  const accept = async (id) => { await TurfTalk.acceptConnection(id); loadAll(); };
  const decline = async (id) => { await TurfTalk.declineConnection(id); loadAll(); };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Messages" subtitle="Direct messages and connection requests." />
      <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <div className="space-y-5">
          <div className="tt-panel rounded-xl p-4">
            <div className="font-display tracking-widest text-xs text-white/40 mb-2">ADD CONNECTION</div>
            <div className="flex gap-2">
              <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@handle" data-testid="connect-handle" className="flex-1 h-10 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
              <button onClick={request} data-testid="connect-request" className="w-10 h-10 grid place-items-center rounded-lg bg-[#2F7D32] text-white"><UserPlus size={16} /></button>
            </div>
          </div>

          {invites.incoming?.length > 0 && (
            <div className="tt-panel rounded-xl p-4">
              <div className="font-display tracking-widest text-xs text-white/40 mb-2">REQUESTS</div>
              {invites.incoming.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between py-2">
                  <span className="text-sm text-white">@{iv.from_handle || iv.handle || iv.from_user_id}</span>
                  <div className="flex gap-1">
                    <button onClick={() => accept(iv.id)} className="w-8 h-8 grid place-items-center rounded-lg bg-[#2F7D32] text-white"><Check size={14} /></button>
                    <button onClick={() => decline(iv.id)} className="w-8 h-8 grid place-items-center rounded-lg tt-panel-3 text-white/70"><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="tt-panel rounded-xl p-2">
            <div className="font-display tracking-widest text-xs text-white/40 px-2 py-2">CONVERSATIONS</div>
            {convos === null ? <Skeleton className="h-16 m-2" />
              : convos.length === 0 ? <p className="text-white/40 text-sm px-2 pb-3">No conversations yet.</p>
              : convos.map((c) => (
                <button key={c.id} onClick={() => setActive(c)} data-testid={`convo-${c.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm ${active?.id === c.id ? "bg-[#24362A] text-white" : "text-white/70 hover:bg-white/5"}`}>
                  {c.title || c.name || (c.members ? `Group (${c.members.length})` : "Direct message")}
                </button>
              ))}
          </div>
        </div>

        <div>
          {active ? (
            <Chat title={active.title || "Direct message"} height={560}
              contextType="dm" contextRefId={active.id}
              loadFn={(p) => TurfTalk.conversationMessages(active.id, p)}
              sendFn={(b) => TurfTalk.postConversationMessage(active.id, b)} />
          ) : (
            <Empty title="Select a conversation" detail="Or add a connection to start a new direct message." />
          )}
        </div>
      </div>
    </div>
  );
}
